import React from 'react'
import { View, StyleSheet, useWindowDimensions } from 'react-native'
import type { SharedValue } from 'react-native-reanimated'
import { Canvas, LinearGradient, Path, BlurMask, vec, Skia } from '@shopify/react-native-skia'
import { useDerivedValue, withSpring } from 'react-native-reanimated'

interface BottomGlowProps {
  /** Omit to render a static silhouette at the quiet baseline (Nora-speaking glow). */
  inputLevel?: SharedValue<number>
  userSpeaking?: boolean
  colors?: string[]
  positions?: number[]
  opacity?: number
  blur?: number
}

const INPUT_LEVEL_SPRING = {
  mass: 0.5,
  damping: 12,
  stiffness: 100,
}

const BOTTOM_GLOW_COLORS = ['#FF1418', '#D54AFF']

export const resolveBottomGlowLevel = (level: number, userSpeaking: boolean) => {
  'worklet'
  if (!userSpeaking) {
    return 0
  }
  return Math.max(0, Math.min(1, level))
}

export const BottomHorizontalGlow = ({
  inputLevel,
  userSpeaking = false,
  colors = BOTTOM_GLOW_COLORS,
  positions,
  opacity = 0.35,
  blur = 60,
}: BottomGlowProps) => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const audioLevel = useDerivedValue(
    () =>
      withSpring(
        // No shared value means no audio reactivity: hold the quiet baseline silhouette.
        inputLevel ? resolveBottomGlowLevel(inputLevel.value, userSpeaking) : 0,
        INPUT_LEVEL_SPRING,
      ),
    [inputLevel, userSpeaking],
  )

  // Give the canvas vertical room (30% of screen height) to accommodate loud peaks
  const canvasHeight = screenHeight * 0.45

  // Derive the active path height and curve based on the current audio level
  const path = useDerivedValue(() => {
    // How far the silhouette rises off the bottom: quiet keeps it low, loud stretches it up.
    const amplitude = 0.55 + audioLevel.value * 3

    // Depth of the design's crest, measured from the bottom of the canvas upward, then scaled by
    // the current audio level. Overshooting the canvas edges keeps the blur from clipping.
    const y = (depth: number) => canvasHeight - canvasHeight * depth * amplitude

    const p = Skia.Path.Make()
    // Start below the bottom left, outside the canvas
    p.moveTo(-60, canvasHeight + 60)

    // Tall left wall
    p.lineTo(1, y(1.5))

    // Fall away to the low plateau left of centre
    p.cubicTo(screenWidth * 0.06, y(0.5), screenWidth * 0.1, y(0.14), screenWidth * 0.27, y(0.3))

    // Rise to the crest just right of centre
    p.cubicTo(screenWidth * 0.4, y(0.34), screenWidth * 0.48, y(0.76), screenWidth * 0.57, y(0.6))

    // Dip back down after the crest
    p.cubicTo(screenWidth * 0.66, y(0.45), screenWidth * 0.68, y(0.22), screenWidth * 0.72, y(0.5))

    // Shallow rise across the right plateau
    p.cubicTo(screenWidth * 0.8, y(0.23), screenWidth * 0.84, y(0.19), screenWidth * 0.88, y(0.34))

    // Sweep up into the tall right wall
    p.cubicTo(screenWidth * 0.95, y(0.1), screenWidth * 0.99, y(1.2), screenWidth + 60, y(2))

    // Close down the right edge and along the bottom
    p.lineTo(screenWidth + 60, canvasHeight + 60)
    p.close()

    return p
  }, [screenWidth, canvasHeight])

  return (
    <View style={[styles.container, { height: canvasHeight }]} pointerEvents="none">
      <Canvas style={styles.canvas}>
        {/* No mask: the blur alone feathers the top edge, matching the design. A vertical fade
            mask dims the crest along with the edge, which washes the gradient out. */}
        <Path path={path} opacity={opacity}>
          <LinearGradient
            start={vec(-60, 0)}
            end={vec(screenWidth + 60, 0)}
            colors={colors}
            positions={positions}
          />
          {/* Soft BlurMask for ambient diffusion */}
          <BlurMask blur={blur} style="normal" />
        </Path>
      </Canvas>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: -1,
  },
  canvas: {
    flex: 1,
  },
})
